/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { GramCore } from "../core.js";
import { encodeJSON, encodeSimple } from "../lib/encodings.js";
import { matchStatusCode } from "../lib/http.js";
import * as M from "../lib/matchers.js";
import { compactMap } from "../lib/primitives.js";
import { safeParse } from "../lib/schemas.js";
import { RequestOptions } from "../lib/sdks.js";
import { resolveSecurity } from "../lib/security.js";
import { pathToFunc } from "../lib/url.js";
import {
  AdminOpenRouterKey,
  AdminOpenRouterKey$inboundSchema,
} from "../models/components/adminopenrouterkey.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import {
  ServiceError,
  ServiceError$inboundSchema,
} from "../models/errors/serviceerror.js";
import {
  EnableAdminOpenRouterKeyRequest,
  EnableAdminOpenRouterKeyRequest$outboundSchema,
  EnableAdminOpenRouterKeySecurity,
} from "../models/operations/enableadminopenrouterkey.js";
import { APICall, APIPromise } from "../types/async.js";
import { Result } from "../types/fp.js";

/**
 * enableKey adminOpenRouterKeys
 *
 * @remarks
 * Reinstate a disabled platform OpenRouter key, upstream and locally, keeping its recorded credit ceiling. Requires platform admin.
 */
export function adminOpenRouterKeysEnableKey(
  client: GramCore,
  request: EnableAdminOpenRouterKeyRequest,
  security?: EnableAdminOpenRouterKeySecurity | undefined,
  options?: RequestOptions,
): APIPromise<
  Result<
    AdminOpenRouterKey,
    | ServiceError
    | GramError
    | ResponseValidationError
    | ConnectionError
    | RequestAbortedError
    | RequestTimeoutError
    | InvalidRequestError
    | UnexpectedClientError
    | SDKValidationError
  >
> {
  return new APIPromise($do(
    client,
    request,
    security,
    options,
  ));
}

async function $do(
  client: GramCore,
  request: EnableAdminOpenRouterKeyRequest,
  security?: EnableAdminOpenRouterKeySecurity | undefined,
  options?: RequestOptions,
): Promise<
  [
    Result<
      AdminOpenRouterKey,
      | ServiceError
      | GramError
      | ResponseValidationError
      | ConnectionError
      | RequestAbortedError
      | RequestTimeoutError
      | InvalidRequestError
      | UnexpectedClientError
      | SDKValidationError
    >,
    APICall,
  ]
> {
  const parsed = safeParse(
    request,
    (value) => z.parse(EnableAdminOpenRouterKeyRequest$outboundSchema, value),
    "Input validation failed",
  );
  if (!parsed.ok) {
    return [parsed, { status: "invalid" }];
  }
  const payload = parsed.value;
  const body = encodeJSON("body", payload.EnableOpenRouterKeyRequestBody, {
    explode: true,
  });

  const path = pathToFunc("/rpc/adminOpenRouterKeys.enableKey")();

  const headers = new Headers(compactMap({
    "Content-Type": "application/json",
    Accept: "application/json",
    "Gram-Session": encodeSimple("Gram-Session", payload["Gram-Session"], {
      explode: false,
      charEncoding: "none",
    }),
  }));

  const requestSecurity = resolveSecurity(
    [
      {
        fieldName: "Gram-Session",
        type: "apiKey:header",
        value: security?.sessionHeaderGramSession,
      },
    ],
  );

  const context = {
    options: client._options,
    baseURL: options?.serverURL ?? client._baseURL ?? "",
    operationID: "enableAdminOpenRouterKey",
    oAuth2Scopes: null,

    resolvedSecurity: requestSecurity,

    securitySource: security,
    retryConfig: options?.retries
      || client._options.retryConfig
      || { strategy: "none" },
    retryCodes: options?.retryCodes || ["429", "500", "502", "503", "504"],
  };

  const requestRes = client._createRequest(context, {
    security: requestSecurity,
    method: "POST",
    baseURL: options?.serverURL,
    path: path,
    headers: headers,
    body: body,
    userAgent: client._options.userAgent,
    timeoutMs: options?.timeoutMs || client._options.timeoutMs || -1,
  }, options);
  if (!requestRes.ok) {
    return [requestRes, { status: "invalid" }];
  }
  const req = requestRes.value;

  const doResult = await client._do(req, {
    context,
    isErrorStatusCode: (statusCode: number) =>
      matchStatusCode({ status: statusCode } as Response, ["4XX", "5XX"]),
    retryConfig: context.retryConfig,
    retryCodes: context.retryCodes,
  });
  if (!doResult.ok) {
    return [doResult, { status: "request-error", request: req }];
  }
  const response = doResult.value;

  const responseFields = {
    HttpMeta: { Response: response, Request: req },
  };

  const [result] = await M.match<
    AdminOpenRouterKey,
    | ServiceError
    | GramError
    | ResponseValidationError
    | ConnectionError
    | RequestAbortedError
    | RequestTimeoutError
    | InvalidRequestError
    | UnexpectedClientError
    | SDKValidationError
  >(
    M.json(200, AdminOpenRouterKey$inboundSchema),
    M.jsonErr([400, 401, 403, 404, 409, 415, 422], ServiceError$inboundSchema),
    M.jsonErr([500, 502], ServiceError$inboundSchema),
    M.fail("4XX"),
    M.fail("5XX"),
  )(response, req, { extraFields: responseFields });
  if (!result.ok) {
    return [result, { status: "complete", request: req, response }];
  }

  return [result, { status: "complete", request: req, response }];
}
